import { Instance, SnapshotIn, types } from "mobx-state-tree";
import { SurveyRuleConditionValidationError } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleConditionValidationError";
import {
  getSurveyRuleValidationErrorValues,
  SurveyRuleCommonValidationError,
} from "../../utils/getSurveyRuleValidationErrorValues";

type ConditionKeyValidationError = Extract<
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_RESPONDENT_KEY
>;

const conditionKeyValidationErrorValues = Object.values(
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
).filter((validationError): validationError is ConditionKeyValidationError =>
  [
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_RESPONDENT_KEY,
  ].includes(validationError),
);

type ConditionValueValidationError = Extract<
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
  | SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_OPTION_CODE_VALUE
  | SurveyRuleConditionValidationError.SurveyRuleConditionValidationError.INVALID_RESPONDENT_VALUE
>;

const conditionValueValidationErrorValues = Object.values(
  SurveyRuleConditionValidationError.SurveyRuleConditionValidationError,
).filter((validationError): validationError is ConditionValueValidationError =>
  [
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_OPTION_CODE_VALUE,
    SurveyRuleConditionValidationError.SurveyRuleConditionValidationError
      .INVALID_RESPONDENT_VALUE,
  ].includes(validationError),
);

export const SurveyRuleRespondentKeyTypeModel = types
  .model("SurveyRuleRespondentKeyTypeModel", {
    type: types.literal("RESPONDENT"),
    conditionKey: types.maybeNull(types.string),
    conditionKeyValidationError: types.array(
      types.enumeration(
        "conditionKeyValidationError",
        getSurveyRuleValidationErrorValues(conditionKeyValidationErrorValues),
      ),
    ),
    conditionValue: types.array(types.string),
    conditionValueValidationError: types.array(
      types.enumeration(
        "conditionValueValidationError",
        getSurveyRuleValidationErrorValues(conditionValueValidationErrorValues),
      ),
    ),
  })
  .actions((store) => ({
    setConditionKey: (conditionKey: string | undefined): void => {
      store.conditionKeyValidationError.clear();
      store.conditionValueValidationError.clear();
      store.conditionKey = conditionKey ?? null;
      store.conditionValue.clear();
    },
    setConditionValue: (conditionValue: string[]): void => {
      store.conditionValueValidationError.clear();
      if (!conditionValue.length) {
        store.conditionValueValidationError.push(
          SurveyRuleCommonValidationError.EMPTY_VALUE,
        );
      }

      store.conditionValue.replace(conditionValue);
    },
    clearValidationErrors: (): void => {
      store.conditionKeyValidationError.clear();
      store.conditionValueValidationError.clear();
    },
    clearConditionKeyValidationErrors: (): void => {
      store.conditionKeyValidationError.clear();
    },
    setConditionKeyValidationError: (
      validationError: ConditionKeyValidationError,
    ): void => {
      store.conditionKeyValidationError.clear();
      store.conditionKeyValidationError.push(validationError);
    },
    setValidationError: (
      validationError:
        | ConditionKeyValidationError
        | ConditionValueValidationError,
    ): void => {
      switch (validationError) {
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_RESPONDENT_KEY:
          store.conditionKeyValidationError.push(validationError);
          break;
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_OPTION_CODE_VALUE:
        case SurveyRuleConditionValidationError
          .SurveyRuleConditionValidationError.INVALID_RESPONDENT_VALUE:
          store.conditionValueValidationError.push(validationError);
          break;
        default:
          validationError satisfies never;
      }
    },
  }));

export const createSurveyRuleRespondentKeyTypeModel = (
  surveyRuleQuestionKeyType?: SnapshotIn<
    typeof SurveyRuleRespondentKeyTypeModel
  >,
): Instance<typeof SurveyRuleRespondentKeyTypeModel> =>
  SurveyRuleRespondentKeyTypeModel.create(
    surveyRuleQuestionKeyType ?? {
      type: "RESPONDENT",
      conditionKey: null,
      conditionKeyValidationError: [],
      conditionValue: [],
      conditionValueValidationError: [],
    },
  );
